import React, { useEffect } from "react";
import { Outlet, useNavigate, useLocation, matchPath } from "react-router-dom";
import "./MainLayout.css";
import HeaderShell from "../components/HeaderShell";
import { AboutSection } from "../pages/FooterSections/AboutSection";
import ContactUs from "../pages/FooterSections/ContactSection";
import useSession from "../hook/useSession";
import useUserActivity from "../hook/useUserActivity";
import { routeConfig } from "../config/routeConfig";

export const MainLayout: React.FC = () => {
  const { isLoggedIn, sessionExpired, clearSessionExpired } = useSession({ checkOnFocus: false });
  const navigate = useNavigate();
  const location = useLocation();

  const currentRoute = routeConfig.main.find((r) => {
    const fullPath = r.path.startsWith("/") ? r.path : `/${r.path}`;
    return matchPath({ path: fullPath, end: true }, location.pathname);
  });

  const isSecure = currentRoute?.isSecure ?? false;

  // ✅ Session resolved = no longer in the initial null loading state
  const sessionResolved = isLoggedIn !== null;

  useEffect(() => {
    // ✅ Guard 1: Don't do anything until session has fully initialized
    if (!sessionResolved) return;

    const isAuthRoute = location.pathname.startsWith("/auth");

    // 1) If visiting secure route and not logged in, reroute to auth
    if (isSecure && isLoggedIn !== true && !isAuthRoute) {
      navigate("/auth", {
        replace: true,
        state: { returnTo: location.pathname + location.search },
      });
      return;
    }

    // 2) If session expired on any route, keep behavior for secure/public
    if (sessionExpired) {
      if (isSecure && !isAuthRoute) {
        // secure route + expired → redirect to login (same as not logged in)
        navigate("/auth", {
          replace: true,
          state: { returnTo: location.pathname + location.search },
        });
      } else {
        // public route: clear stale expiration marker
        clearSessionExpired();
      }
    }
  }, [sessionExpired, isSecure, isLoggedIn, sessionResolved, navigate, location, clearSessionExpired]);

  useUserActivity(() => {});

  // ✅ Show loading until session is resolved (prevents premature redirects)
  if (!sessionResolved) {
    return (
      <div className="loading-container" style={{ textAlign: "center", marginTop: "50px" }}>
        Loading session...
      </div>
    );
  }

  return (
    <div className="main-layout" style={{ background: "var(--bg)" }}>
      <header className="main-header">
        <HeaderShell isLoggedIn={Boolean(isLoggedIn)} />
      </header>
      <main className="main-content">
        <Outlet />
      </main>
      <footer style={{ background: "var(--bg)" }} className="main-footer">
        <AboutSection />
        <ContactUs />
      </footer>
    </div>
  );
};